import { ExternalLink, PlayCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { formatDate } from '@/utils/format'
import { KIND_META } from '../kinds'
import type { Material } from '../types'
import { FileChips } from './FileChips'
import { RichText } from './RichText'
import { VideoBlock } from './VideoPlayer'
import { VideoEmbed } from '@/components/ui/VideoEmbed'
import { embedUrl } from '@/utils/embed'

/** One material as a card: kind, title, text, link and files; actions for teachers. */
export function MaterialCard({ material, actions }: { material: Material; actions?: ReactNode }) {
  const { t } = useTranslation('lms')
  const meta = KIND_META[material.kind]
  const Icon = meta.icon
  return (
    <article className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600">
          <Icon className="size-[1.125rem]" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[0.9375rem] font-semibold text-ink">{material.title}</h3>
            {!material.published ? (
              <Badge tone="warning" size="sm">
                {t('Draft')}
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted">
            {t(meta.label)}
            {material.author ? ` · ${material.author}` : ''}
            {material.publishedAt ? ` · ${formatDate(material.publishedAt)}` : ''}
          </p>
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      </div>
      {material.body || material.url || material.video || material.files.length ? (
        <div className="mt-3 space-y-3 sm:pl-12">
          {material.video ? <VideoBlock video={material.video} title={material.title} /> : null}
          {material.kind === 'video' && embedUrl(material.url) ? (
            <VideoEmbed url={material.url ?? ''} title={material.title} />
          ) : null}
          {material.body ? <RichText text={material.body} /> : null}
          {material.url && !(material.kind === 'video' && embedUrl(material.url)) ? (
            <a
              href={material.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-2 rounded-control bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100"
            >
              {material.kind === 'video' ? (
                <PlayCircle className="size-4 shrink-0" aria-hidden="true" />
              ) : (
                <ExternalLink className="size-4 shrink-0" aria-hidden="true" />
              )}
              <span className="truncate">
                {material.kind === 'video' ? t('Watch the video') : t('Open the link')}
              </span>
            </a>
          ) : null}
          <FileChips files={material.files} />
        </div>
      ) : null}
    </article>
  )
}
